import {noop} from '@benchling/js-utils';
import produce from 'immer';
import React, {useState, useCallback} from 'react';
import {NodeProps, useReactFlow} from 'reactflow';

import {ProcessFlowchartNodeDetailsType} from 'graphql-client/__generated/graphqlGlobalTypes';
import {PLUGIN_REGISTRY_INSTANCE} from 'processes/graph-builder-processes/platform/PluginRegistry';
import {isStartOrEndNode} from 'processes/settings/graph-builder-flowchart-config/horizontal-flowchart/adapterUtils';
import {getFieldInfoFromDataFlowEdgeHandle} from 'processes/settings/graph-builder-flowchart-config/horizontal-flowchart/flowchartIdentifierUtils';
import {
  convertRootRowToMapFromStartRow,
  filterToAddableMapFromStartRows,
} from 'processes/settings/graph-builder-flowchart-config/horizontal-flowchart/mapFromStartUtils';
import {
  ProcessFlowchartEdgeType,
  TaskSchemaNodeSectionType,
} from 'processes/settings/graph-builder-flowchart-config/horizontal-flowchart/processFlowchartConfigEditorConstants';
import {
  TaskSchemaNodeData,
  TaskSchemaNodeRowData,
  TaskSchemaNodeSectionData,
} from 'processes/settings/graph-builder-flowchart-config/horizontal-flowchart/processFlowchartConfigEditorNodeTypes';
import {FlowchartNodeChangeType} from 'processes/settings/graph-builder-flowchart-config/horizontal-flowchart/processFlowchartConfigEditorUtils';
import {FlowchartConfigWithMetadata} from 'processes/settings/graph-builder-flowchart-config/horizontal-flowchart/redux/flowchartConfigAutosaveManagerCallbacks';
import {getCustomMappingForEdge} from 'processes/settings/graph-builder-flowchart-config/horizontal-flowchart/types/transition/transitionLookupUtils';
import {useTransitionsContext} from 'processes/settings/graph-builder-flowchart-config/horizontal-flowchart/types/transition/TransitionsContext';
import useOnMount from 'ui-library/hooks/useOnMount';
import {ReactFlowLockedContext} from 'ui-library/reactflow/LockedContext';
import {BenchlingNode} from 'ui-library/reactflow/nodes/BenchlingNode/BenchlingNode';
import FlowchartNodeToolbar from 'ui-library/reactflow/nodes/toolbar/FlowchartNodeToolbar';

import {EdgesContext} from '../EdgesContext';
import {FlowchartFeaturesContext, isFlowchartFeatureEnabled} from '../FlowchartFeaturesContext';
import {NodesContext} from '../NodesContext';
import {ProcessFlowchartEditorModeFeatureType} from '../ProcessFlowchartEditorModeConstants';
import AddFieldToSectionButton from './AddFieldToSectionButton';
import createHeaderPropsForNode from './createHeaderPropsForNode';
import {getNodeAndRowStatusAndDescription} from './nodeUtils';
import {FlowchartNodeEditMode} from './ProcessFlowchartNodeAdapter';
import {TaskSchemaNodeRowRenderer} from './TaskSchemaNodeRowRenderer';
import {waitForAnimationFrames} from './waitForAnimationFrames';

/**
 * When a node is created with isEditingNodeName: true, React Flow hasn't
 * positioned it on the canvas yet. This hook defers the initial editing
 * state to allow React Flow to settle first, so that focusOnMount in
 * FormInput works properly.
 *
 * Only defers on initial mount - subsequent state changes (e.g., from
 * user double-click) are passed through immediately.
 */
function useDeferredInitialEditingState(isEditingNodeName: boolean, canNodeNameBeEdited: boolean): boolean {
  const needsInitialDefer = isEditingNodeName && canNodeNameBeEdited;
  const [isReadyToEdit, setIsReadyToEdit] = useState(!needsInitialDefer);

  useOnMount(() => {
    if (!isReadyToEdit) {
      (async () => {
        await waitForAnimationFrames(3);
        setIsReadyToEdit(true);
      })();
    }
  });

  if (!isReadyToEdit) {
    return false;
  }

  return isEditingNodeName;
}

export default function TaskSchemaNodeRenderer(props: NodeProps<TaskSchemaNodeData>): JSX.Element {
  const {data, xPos, yPos, id} = props;
  // Row and handle hovers do no propagate up. This hovered state primarily indicates hovering on the header
  const [isHovered, setIsHovered] = useState(false);
  const reactFlowInstance = useReactFlow();
  const {sections, onNodeHeaderClick, onNodeDelete, onUpdateNodeData} = data;
  const locked = ReactFlowLockedContext.useContext() ?? false;

  const headerProps = createHeaderPropsForNode(
    {
      ...data,
      position: {x: xPos, y: yPos},
    },
    reactFlowInstance,
    data.nodeDetailsType !== ProcessFlowchartNodeDetailsType.ROOT_NODE_DETAILS,
    data.nodeDetailsType !== ProcessFlowchartNodeDetailsType.OUTPUT_NODE_DETAILS,
    locked
  );

  const nodesContext = NodesContext.useContext();
  const edgesContext = EdgesContext.useContext();
  const flowchartFeatures = FlowchartFeaturesContext.useContext();
  const {transitionsById, ptdvPairToTransitionIdIndex} = useTransitionsContext();

  const executionType =
    data.taskDefinitionVersionSpec.newProcessTaskDefinitionVersion.taskSchema.executionType;
  const adapter = PLUGIN_REGISTRY_INSTANCE.getTaskSchemaNodeAdapter(executionType);
  const isDeletable =
    adapter.isDeletable() ||
    (isStartOrEndNode(data.nodeDetailsType) &&
      isFlowchartFeatureEnabled(
        flowchartFeatures,
        ProcessFlowchartEditorModeFeatureType.ALLOW_START_END_NODE_DELETION
      ));
  const showNodeSections = isFlowchartFeatureEnabled(
    flowchartFeatures,
    ProcessFlowchartEditorModeFeatureType.SHOW_NODE_SECTIONS_AND_DATA_FLOW_EDGES
  );
  const isDoubleClickEditMode = adapter.editMode === FlowchartNodeEditMode.DOUBLE_CLICK;
  const isNodeNameEditableInline = adapter.isNodeNameEditableInline && !locked;

  const isEditingName = useDeferredInitialEditingState(
    Boolean(data.isEditingNodeName),
    isNodeNameEditableInline
  );
  const setIsEditingName = useCallback(
    (editing: boolean) => {
      if (!isNodeNameEditableInline) {
        return;
      }
      onUpdateNodeData((flowchartConfig: FlowchartConfigWithMetadata) => {
        const currentNodeData = flowchartConfig.staticFlowchart.nodesStaticData.find(
          (node) => node.id === data.id
        );
        if (currentNodeData == null) {
          return [];
        }
        const newNodeData = {...currentNodeData, isEditingNodeName: editing};
        return [{id: data.id, data: newNodeData, type: FlowchartNodeChangeType.REPLACE}];
      });
    },
    [data.id, isNodeNameEditableInline, onUpdateNodeData]
  );

  const handleNodeNameComplete = useCallback(
    (newName: string) => {
      if (newName.trim() === '' || newName === data.nodeName) {
        return;
      }
      const updatedSpec = adapter.onUpdateNodeName(data.taskDefinitionVersionSpec, newName);
      if (updatedSpec == null) {
        return;
      }

      onUpdateNodeData((flowchartConfig: FlowchartConfigWithMetadata) => {
        const newNodeData = produce(data, (draftNode) => {
          draftNode.nodeName = newName;
          draftNode.taskDefinitionVersionSpec = updatedSpec;
        });
        return [{id: data.id, data: newNodeData, type: FlowchartNodeChangeType.REPLACE}];
      });
    },
    [adapter, data, onUpdateNodeData]
  );

  const editClickHandler =
    onNodeHeaderClick != null && !isEditingName && !isDoubleClickEditMode ? {onClick: onNodeHeaderClick} : {};

  // Check if lookup is configured for a specific row
  const isLookupConfiguredForRow = (nodeId: string, rowClientId: string): boolean => {
    const staticNodes = nodesContext.nodes.map((node) => node.data);

    const relevantEdges = edgesContext.edges.filter(
      (edge) =>
        edge.source === nodeId &&
        edge.type === ProcessFlowchartEdgeType.DATA_FLOW &&
        edge.sourceHandle &&
        getFieldInfoFromDataFlowEdgeHandle(edge.sourceHandle).fieldClientId === rowClientId
    );

    return relevantEdges.some((edge) => {
      const customMapping = getCustomMappingForEdge({
        edge,
        nodes: staticNodes,
        transitionsById,
        ptdvPairToTransitionIdIndex,
      });
      return customMapping?.lookupConfig != null;
    });
  };

  const {nodeStatus, nodeDescription, rowStatusAndDescriptionById} = getNodeAndRowStatusAndDescription(
    data.validationErrors
  );

  const mapFromStartSection = data.sections.find(
    (section) => section.sectionType === TaskSchemaNodeSectionType.MAP_FROM_START
  );
  let addFieldToMapFromStartSectionButton: JSX.Element | null;
  if (mapFromStartSection) {
    const rootRows = nodesContext.startNode?.sections.flatMap((section) => section.sectionRows) ?? [];
    const addableRows = filterToAddableMapFromStartRows(rootRows, mapFromStartSection.sectionRows);
    addFieldToMapFromStartSectionButton = (
      <AddFieldToSectionButton
        rows={addableRows}
        icon="plus"
        disabled={locked || addableRows.length === 0}
        onAddFieldToSection={(rowData: TaskSchemaNodeRowData) => {
          onUpdateNodeData(
            (flowchartConfig: FlowchartConfigWithMetadata) => {
              const newNodeData = produce(data, (draftNode) => {
                draftNode.sections = draftNode.sections.map((section) => {
                  if (section.sectionType === TaskSchemaNodeSectionType.MAP_FROM_START) {
                    section.sectionRows.push(convertRootRowToMapFromStartRow(rowData));
                    return section;
                  }
                  return section;
                });
              });
              return [{id: data.id, data: newNodeData, type: FlowchartNodeChangeType.REPLACE}];
            },
            false,
            true // Skip setting dirty state to prevent autosave when adding map from start field
          );
        }}
      />
    );
  } else {
    addFieldToMapFromStartSectionButton = null;
  }

  const isArchived = data.isArchived || false;
  // A node is visually disabled if the underlying schema is archived OR the node itself
  // is marked non-interactive (e.g., config-level gating).
  const isNodeDisabled = data.isDisabled || isArchived;
  const disabledTooltip = isArchived ? 'Remove or replace this archived task schema' : undefined;
  // Archived nodes can still be deleted (so users can clean them up), but other disabled
  // nodes cannot.
  const isDisabledButNotArchived = isNodeDisabled && !isArchived;
  // A locked flowchart always blocks deletion.
  const isNodeDeletable = isDeletable && !locked && !isDisabledButNotArchived;

  return (
    <>
      <FlowchartNodeToolbar
        isLocked={locked || isNodeDisabled}
        onNodeEdit={isDoubleClickEditMode ? onNodeHeaderClick : undefined}
        onNodeDelete={isNodeDeletable ? onNodeDelete : undefined}
      />
      <BenchlingNode
        isSelected={props.selected}
        isDisabled={isNodeDisabled}
        status={nodeStatus}
        statusDescription={nodeDescription}
        onMouseOver={() => {
          setIsHovered(true);
        }}
        onMouseOut={() => {
          setIsHovered(false);
        }}
        dataTestAttrs={{
          element: 'TaskSchemaNodeRenderer',
          key: headerProps.title,
        }}
      >
        {headerProps && (
          <BenchlingNode.Standard.Header
            {...headerProps}
            {...editClickHandler}
            disabledTooltip={disabledTooltip}
            style={{
              cursor: onNodeHeaderClick != null ? 'pointer' : 'default',
            }}
            doubleClickEditableTitleProps={
              isNodeNameEditableInline
                ? {
                    isEditing: isEditingName,
                    setIsEditing: setIsEditingName,
                    onComplete: handleNodeNameComplete,
                    onDraftNameChanged: noop,
                    defaultValue: () => data.nodeName,
                  }
                : undefined
            }
          />
        )}
        {showNodeSections && sections && sections.length > 0 && (
          <BenchlingNode.Sections>
            {sections.map((section: TaskSchemaNodeSectionData) => (
              <BenchlingNode.Section
                key={section.sectionName}
                leftHeader={section.sectionName}
                rightHeader={
                  section.sectionType === TaskSchemaNodeSectionType.MAP_FROM_START
                    ? addFieldToMapFromStartSectionButton
                    : undefined
                }
              >
                {section.sectionRows.map((row) => (
                  <TaskSchemaNodeRowRenderer
                    key={row.id}
                    {...row}
                    status={rowStatusAndDescriptionById[row.clientId]?.status ?? undefined}
                    statusDescription={rowStatusAndDescriptionById[row.clientId]?.description ?? undefined}
                    secondaryIcon={isLookupConfiguredForRow(id, row.clientId) ? 'entity-lookup' : undefined}
                    secondaryText={row.secondaryText}
                    isHovered={isHovered}
                  />
                ))}
              </BenchlingNode.Section>
            ))}
          </BenchlingNode.Sections>
        )}
      </BenchlingNode>
    </>
  );
}
